/**
 * Dashboard Statistics Hook
 * Custom hook for managing dashboard statistics state
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import StatsService from '../services/StatsService';

export const useStats = (options = {}) => {
  const { 
    autoRefresh = true, 
    refreshInterval = 5 * 60 * 1000, // 5 minutes
    category = null // null for all stats, or 'inventory', 'reservations', 'customers', 'payments'
  } = options;

  const { token, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fromCache, setFromCache] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchStats = useCallback(async (force = false) => {
    if (!token || authLoading) return;

    try {
      setLoading(true);
      setError(null);

      let data;
      if (force) {
        data = await StatsService.refreshStats(token);
      } else if (category) {
        // Fetch specific category
        switch (category) {
          case 'inventory':
            data = await StatsService.getInventoryStats(token);
            break;
          case 'reservations':
            data = await StatsService.getReservationStats(token);
            break;
          case 'customers':
            data = await StatsService.getCustomerStats(token);
            break;
          case 'payments':
            data = await StatsService.getPaymentStats(token);
            break;
          default:
            data = await StatsService.getAllStats(token);
        }
      } else {
        data = await StatsService.getAllStats(token);
      }

      setStats(data);
      setFromCache(data.fromCache || false);
      setLastUpdated(data.lastUpdated || new Date());
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, authLoading, category]);

  const refresh = useCallback(() => {
    return fetchStats(true);
  }, [fetchStats]);

  // Initial fetch
  useEffect(() => {
    if (token && !authLoading) {
      fetchStats();
    }
  }, [token, authLoading, fetchStats]);

  // Auto-refresh interval
  useEffect(() => {
    if (!autoRefresh || !token || authLoading) return;

    const interval = setInterval(() => {
      fetchStats();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, token, authLoading, fetchStats]);

  return {
    stats,
    loading,
    error,
    fromCache,
    lastUpdated,
    refresh,
    refetch: fetchStats
  };
};

export const useInventoryStats = (options = {}) => {
  return useStats({ ...options, category: 'inventory' });
};

export const useReservationStats = (options = {}) => {
  return useStats({ ...options, category: 'reservations' });
};

export const useCustomerStats = (options = {}) => {
  return useStats({ ...options, category: 'customers' });
};

export const usePaymentStats = (options = {}) => {
  return useStats({ ...options, category: 'payments' });
};